<div class="account-pages my-5 pt-sm-5">
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card overflow-hidden">
          <div class="bg-primary-subtle">
            <div class="row">
              <div class="col-12">
                <div class="text-primary p-4">
                  <h5 class="text-primary">Organization Invitation</h5>
                  <p>You've been invited to join a team on Echo</p>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body pt-0">
            <div class="p-2 mt-3">

              <!-- Loading -->
              <div *ngIf="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading invitation...</p>
              </div>

              <!-- Error -->
              <div *ngIf="!loading && error" class="text-center py-4">
                <div class="avatar-md mx-auto mb-3">
                  <div class="avatar-title rounded-circle" style="background-color: rgba(239, 68, 68, 0.1); color: #ef4444;">
                    <i class="bx bx-x-circle font-size-24"></i>
                  </div>
                </div>
                <h5 class="text-danger">Invitation Error</h5>
                <p class="text-muted mb-4">{{ error }}</p>
                <a routerLink="/auth/login" class="btn btn-primary">Go to Login</a>
              </div>

              <!-- Invitation Details -->
              <div *ngIf="!loading && !error && invitationData">
                <div class="text-center mb-4">
                  <div class="avatar-md mx-auto mb-3">
                    <div class="avatar-title rounded-circle" style="background-color: rgba(85, 110, 230, 0.1); color: #556ee6;">
                      <i class="bx bx-envelope-open font-size-24"></i>
                    </div>
                  </div>
                  <h5>Join {{ orgName }}</h5>
                  <p class="text-muted">
                    <strong>{{ inviterName }}</strong> has invited you to join as
                    {{ role === 'administrator' ? 'an' : (role === 'support' ? '' : 'a') }}
                    <span class="badge bg-soft-primary text-primary">{{ getRoleDisplayName(role) }}</span>
                  </p>
                </div>

                <div class="alert alert-info mb-4" role="alert">
                  <i class="bx bx-info-circle me-2"></i>
                  By accepting, you'll gain access to {{ orgName }}'s Echo workspace with
                  <strong>{{ getRoleDisplayName(role) }}</strong> permissions.
                  <span *ngIf="role === 'support'">Support Staff answer and assign conversations; the rest of Echo is not available to them.</span>
                </div>

                <!--
                  Signed in as somebody other than the invitee. The server
                  refuses this too; stopping here saves them a rejected Accept.
                -->
                <div *ngIf="signedIn && wrongAccount" class="alert alert-warning" role="alert">
                  <i class="bx bx-user-x me-2"></i>
                  This invitation was sent to <strong>{{ inviteEmail }}</strong>, but you're
                  signed in as <strong>{{ wrongAccount }}</strong>.
                  <div class="mt-2">
                    <button class="btn btn-sm btn-warning" (click)="switchAccount()">
                      Sign in as {{ inviteEmail }}
                    </button>
                  </div>
                </div>

                <!-- Signed in: name and accept -->
                <ng-container *ngIf="signedIn && !wrongAccount">
                  <div class="mb-3">
                    <label class="form-label">Your name</label>
                    <input type="text" class="form-control" [(ngModel)]="userName"
                           name="userName" placeholder="e.g. Martin Naudé"
                           (keyup.enter)="acceptInvitation()" [disabled]="accepting">
                    <div class="form-text">Shown on messages you send, so recipients know who they are talking to.</div>
                  </div>

                  <div class="d-grid gap-2">
                    <button class="btn btn-primary btn-lg"
                            (click)="acceptInvitation()"
                            [disabled]="accepting">
                      <span *ngIf="!accepting">
                        <i class="bx bx-check-circle me-1"></i> Accept Invitation
                      </span>
                      <span *ngIf="accepting">
                        <i class="bx bx-loader-alt bx-spin me-1"></i> Joining...
                      </span>
                    </button>
                  </div>
                </ng-container>

                <!--
                  Signed out. The invitation stays on screen and sign-in happens
                  here, so there is no login page to bounce through and no
                  invitation to lose on the way back.
                -->
                <ng-container *ngIf="!signedIn">

                  <!--
                    Arrived on the invitation's own sign-in link. The address is
                    shown rather than assumed: the link carries the credential as
                    well as the invitation, so a forwarded email would otherwise
                    sign the recipient in as the person who was invited.
                  -->
                  <ng-container *ngIf="pendingSignInLink">
                    <p class="text-muted small mb-3">
                      You're signing in as <strong>{{ inviteEmail }}</strong>, the address this
                      invitation was sent to.
                    </p>

                    <div class="d-grid">
                      <button class="btn btn-primary btn-lg" (click)="continueAsInvitee()"
                              [disabled]="signingIn">
                        <span *ngIf="!signingIn">
                          <i class="bx bx-log-in-circle me-1"></i> Continue as {{ inviteEmail }}
                        </span>
                        <span *ngIf="signingIn">
                          <i class="bx bx-loader-alt bx-spin me-1"></i> Signing in...
                        </span>
                      </button>
                    </div>

                    <div class="mt-3 text-center">
                      <button type="button" class="btn btn-link p-0" (click)="useAnotherAccount()"
                              [disabled]="signingIn">
                        <small>Not you? <span class="fw-medium text-primary">Sign in another way.</span></small>
                      </button>
                    </div>
                  </ng-container>

                  <ng-container *ngIf="!pendingSignInLink">
                    <div *ngIf="signInLinkExpired" class="alert alert-warning" role="alert">
                      <i class="bx bx-time-five me-2"></i>
                      The sign-in link in your invitation has expired or has already been used.
                      The invitation itself is still valid — send yourself a fresh link below.
                    </div>

                    <p class="text-muted small mb-3" *ngIf="!signInLinkExpired">
                      Sign in to accept. Use
                      <strong *ngIf="inviteEmail">{{ inviteEmail }}</strong>
                      <span *ngIf="!inviteEmail">the address this invitation was sent to</span>
                      — the invitation only works for that address.
                    </p>

                    <!--
                      Planning Center is offered only by a PCO organisation. It
                      takes the organisation from its own sign-in claim, so on a
                      standalone invitation it would put the person somewhere else
                      entirely rather than into the organisation that invited them.
                    -->
                    <div class="d-grid gap-2">
                      <button class="btn btn-primary" *ngIf="orgType === 'pco'"
                              (click)="signInWithPlanningCenter()" [disabled]="signingIn">
                        <i class="mdi mdi-shield-account me-2"></i>Sign In With Planning Center
                      </button>

                      <button class="btn btn-outline-secondary" (click)="signInWithMicrosoft()"
                              [disabled]="signingIn">
                        <i class="mdi mdi-microsoft me-2"></i>Continue with Microsoft
                      </button>

                      <button class="btn btn-outline-secondary" *ngIf="!emailFormOpen"
                              (click)="emailFormOpen = true" [disabled]="signingIn">
                        <i class="mdi mdi-email-outline me-2"></i>Continue with email
                      </button>
                    </div>

                    <div class="mt-3" *ngIf="emailFormOpen">
                      <div *ngIf="!emailLinkSent">
                        <label class="form-label small">Email address</label>
                        <div class="input-group">
                          <input type="email" class="form-control" [(ngModel)]="emailAddress"
                                 name="emailAddress" placeholder="you@yourchurch.org"
                                 (keyup.enter)="sendEmailLink()" [disabled]="signingIn">
                          <button class="btn btn-primary" (click)="sendEmailLink()" [disabled]="signingIn">
                            Send link
                          </button>
                        </div>
                        <small class="text-muted">
                          We'll email you a link that signs you in and brings you back here — no password to remember.
                        </small>
                      </div>

                      <div *ngIf="emailLinkSent" class="alert alert-success mb-0">
                        <i class="mdi mdi-email-check-outline me-1"></i>
                        Check <strong>{{ emailAddress }}</strong> for your sign-in link. It's valid for one use.
                      </div>
                    </div>
                  </ng-container>
                </ng-container>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
